import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { api, SEASON_LABELS } from "../lib/api";
import { toast, Toaster } from "sonner";
import { Clock, User, Plus, ArrowLeft, FileText, Pencil, Trash2, Save, X, Flag } from "lucide-react";
import { useAuth } from "../lib/auth";
import { saveEditToken, getEditToken, removeEditToken } from "../lib/editTokens";

const TIMESTAMP_REGEX = /^\d{1,2}:\d{2}(:\d{2})?$/;

export default function EpisodeDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = !!user;
  const [ep, setEp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEpisode, setEditingEpisode] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/episodes/${id}`)
      .then((r) => setEp(r.data))
      .catch(() => setEp(null))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="brutal-card p-10 text-center font-mono-display uppercase tracking-widest" data-testid="episode-loading">Opening the hatch...</div>;
  }
  if (!ep) {
    return (
      <div className="brutal-card p-10 text-center" data-testid="episode-not-found">
        <div className="font-mono-display uppercase tracking-widest text-pink-600 mb-2">404</div>
        <div className="text-lg mb-4">Episode not found in the bunker.</div>
        <Link to="/episodes" className="brutal-btn primary">← Back to Episodes</Link>
      </div>
    );
  }

  return (
    <div data-testid="episode-detail-page">
      <Toaster position="bottom-right" />
      <Link to="/episodes" className="inline-flex items-center gap-2 font-mono-display text-xs uppercase tracking-widest text-slate-700 hover:text-pink-600 mb-4" data-testid="back-to-episodes">
        <ArrowLeft size={14} strokeWidth={3} /> All Episodes
      </Link>

      {editingEpisode ? (
        <EditEpisodeForm
          ep={ep}
          onCancel={() => setEditingEpisode(false)}
          onSaved={(updated) => { setEp({ ...updated, sections: ep.sections }); setEditingEpisode(false); }}
        />
      ) : (
        <div className="brutal-card p-6 md:p-8 mb-6 relative" data-testid="episode-header">
          {isAdmin && (
            <button
              onClick={() => setEditingEpisode(true)}
              className="brutal-btn absolute top-4 right-4 !py-1 !px-2 text-xs"
              data-testid="edit-episode-button"
            >
              <Pencil size={12} strokeWidth={3} /> Edit
            </button>
          )}
          <div className="flex items-start gap-4 flex-wrap pr-20">
            <div className="bg-yellow-400 brutal-border font-mono-display font-bold text-3xl px-4 py-3 whitespace-nowrap">
              S{ep.season}·E{String(ep.episode_number).padStart(2, "0")}
            </div>
            <div className="flex-1 min-w-[240px]">
              <div className="font-mono-display text-xs uppercase tracking-[0.25em] text-pink-600 mb-1">
                {SEASON_LABELS[ep.season]} · Aired {ep.air_date}
              </div>
              <h1 className="font-mono-display text-3xl sm:text-4xl font-bold tracking-tighter uppercase leading-none">
                {ep.title}
              </h1>
            </div>
          </div>
          <div className="mt-5 pt-5 border-t-2 border-slate-900">
            <div className="font-mono-display text-xs uppercase tracking-widest text-slate-600 mb-2">Synopsis</div>
            <p className="text-base leading-relaxed text-slate-800">{ep.synopsis}</p>
          </div>
        </div>
      )}

      <div className="mb-5 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="font-mono-display text-xs uppercase tracking-[0.3em] text-pink-600 mb-1">Community Log</div>
          <h2 className="font-mono-display text-2xl font-bold tracking-tight border-b-4 border-slate-900 pb-2 uppercase">
            Sections ({ep.sections.length})
          </h2>
        </div>
        <button onClick={() => setShowForm((s) => !s)} className={`brutal-btn ${showForm ? "" : "primary"}`} data-testid="toggle-add-section">
          <Plus size={16} strokeWidth={3} /> {showForm ? "Cancel" : "Add a Section"}
        </button>
      </div>

      {showForm && <AddSectionForm episodeId={ep.id} onAdded={() => { setShowForm(false); load(); }} />}

      {ep.sections.length === 0 ? (
        <div className="brutal-card p-8 text-center" data-testid="sections-empty">
          <FileText size={32} strokeWidth={2.5} className="mx-auto mb-3 text-slate-400" />
          <div className="font-mono-display uppercase tracking-widest text-slate-700 mb-2">&gt; No sections yet.</div>
          <div className="text-sm text-slate-600">Be the first! Drop a timestamp and a headline.</div>
        </div>
      ) : (
        <ul className="space-y-4">
          {ep.sections.map((s) => (
            <SectionEntry key={s.id} s={s} isAdmin={isAdmin} onChanged={load} />
          ))}
        </ul>
      )}
    </div>
  );
}

function SectionEntry({ s, isAdmin, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [headline, setHeadline] = useState(s.headline);
  const [timestamp, setTimestamp] = useState(s.timestamp);
  const [body, setBody] = useState(s.body);
  const [author, setAuthor] = useState(s.author);
  const [saving, setSaving] = useState(false);

  const ownToken = getEditToken(s.id);
  const canEdit = isAdmin || !!ownToken;
  const isOwnerOnly = !isAdmin && !!ownToken;

  const save = async () => {
    if (!headline.trim() || !body.trim() || !timestamp.trim()) {
      toast.error("Headline, timestamp, and body are required."); return;
    }
    if (!TIMESTAMP_REGEX.test(timestamp.trim())) {
      toast.error("Timestamp must look like 12:34 or 1:02:45."); return;
    }
    setSaving(true);
    try {
      if (isAdmin) {
        await api.patch(`/sections/${s.id}`, {
          headline: headline.trim(), timestamp: timestamp.trim(),
          body: body.trim(), author: author.trim() || "Mole Anon",
        });
      } else {
        // Contributor self-edit — author is immutable
        await api.patch(`/sections/${s.id}/self`,
          { headline: headline.trim(), timestamp: timestamp.trim(), body: body.trim() },
          { headers: { "X-Edit-Token": ownToken } },
        );
      }
      toast.success("Section updated.");
      setEditing(false);
      onChanged?.();
    } catch (e) {
      const status = e.response?.status;
      if (status === 403) toast.error("You can't edit this post.");
      else if (status === 401) toast.error(isAdmin ? "Session expired. Sign in again." : "Missing edit permission.");
      else toast.error("Update failed.");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete this section?\n\n"${s.headline}"`)) return;
    try {
      await api.delete(`/sections/${s.id}`);
      toast.success("Section deleted.");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.status === 401 ? "Session expired. Sign in again." : "Delete failed.");
    }
  };

  const removeSelf = async () => {
    if (!window.confirm(`Delete your post permanently?\n\n"${s.headline}"`)) return;
    try {
      await api.delete(`/sections/${s.id}/self`, { headers: { "X-Edit-Token": ownToken } });
      removeEditToken(s.id);
      toast.success("Your post was deleted.");
      onChanged?.();
    } catch (e) {
      const status = e.response?.status;
      if (status === 403) toast.error("You can't delete this post.");
      else if (status === 401) toast.error("Missing edit permission.");
      else toast.error("Delete failed.");
    }
  };

  return (
    <li className="brutal-card overflow-hidden" data-testid={`section-${s.id}`}>
      <div className="bg-slate-900 text-white px-4 py-2 flex flex-wrap items-center gap-3 font-mono-display text-xs uppercase tracking-widest">
        <div className="flex items-center gap-1 bg-yellow-400 text-slate-900 px-2 py-0.5">
          <Clock size={12} strokeWidth={3} /> {s.timestamp}
        </div>
        <div className="flex items-center gap-1 text-slate-300">
          <User size={12} strokeWidth={3} /> {s.author}
        </div>
        {ownToken && (
          <div className="flex items-center gap-1 bg-cyan-400 text-slate-900 px-2 py-0.5" title="You posted this from this browser" data-testid={`yours-badge-${s.id}`}>
            <Pencil size={10} strokeWidth={3} /> YOURS
          </div>
        )}
        <div className="text-slate-500 ml-auto normal-case tracking-normal text-[11px]">
          {new Date(s.created_at).toLocaleString()}
        </div>
        {isAdmin && !editing && (
          <div className="flex gap-1">
            <button onClick={() => setEditing(true)} className="bg-cyan-400 text-slate-900 px-2 py-0.5 hover:bg-cyan-300 flex items-center gap-1" data-testid={`edit-section-${s.id}`}>
              <Pencil size={11} strokeWidth={3} /> Edit
            </button>
            <button onClick={remove} className="bg-red-500 text-white px-2 py-0.5 hover:bg-red-600 flex items-center gap-1" data-testid={`delete-section-${s.id}`}>
              <Trash2 size={11} strokeWidth={3} /> Del
            </button>
          </div>
        )}
        {isOwnerOnly && !editing && (
          <div className="flex gap-1">
            <button onClick={() => setEditing(true)} className="bg-cyan-400 text-slate-900 px-2 py-0.5 hover:bg-cyan-300 flex items-center gap-1" data-testid={`edit-own-section-${s.id}`} title="You posted this — you can edit it">
              <Pencil size={11} strokeWidth={3} /> EDIT
            </button>
            <button onClick={removeSelf} className="bg-red-500 text-white px-2 py-0.5 hover:bg-red-600 flex items-center gap-1" data-testid={`delete-own-section-${s.id}`} title="Delete your post">
              <Trash2 size={11} strokeWidth={3} /> DELETE
            </button>
          </div>
        )}
        {!isAdmin && !isOwnerOnly && (
          <button onClick={() => setReporting(true)} className="bg-slate-700 text-white px-2 py-0.5 hover:bg-red-600 flex items-center gap-1" data-testid={`report-section-${s.id}`} title="Report this section">
            <Flag size={11} strokeWidth={3} /> Report
          </button>
        )}
      </div>
      {reporting && (
        <ReportSectionModal sectionId={s.id} onClose={() => setReporting(false)} />
      )}
      <div className="p-5">
        {editing ? (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_140px] gap-3">
              <input value={headline} onChange={(e) => setHeadline(e.target.value)} className="brutal-input" maxLength={200} placeholder="Headline" data-testid={`edit-headline-${s.id}`} />
              <input value={timestamp} onChange={(e) => setTimestamp(e.target.value)} className="brutal-input font-mono-display text-center" maxLength={8} placeholder="12:34" data-testid={`edit-timestamp-${s.id}`} />
            </div>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} className="brutal-textarea" maxLength={5000} placeholder="Body" data-testid={`edit-body-${s.id}`} />
            {isAdmin ? (
              <input value={author} onChange={(e) => setAuthor(e.target.value)} className="brutal-input" maxLength={60} placeholder="Author" data-testid={`edit-author-${s.id}`} />
            ) : (
              <div className="font-mono-display text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-2">
                <User size={11} strokeWidth={3} /> posted as: <span className="text-slate-900">{s.author}</span> · name is locked
              </div>
            )}
            <div className="flex gap-2">
              <button onClick={save} disabled={saving} className="brutal-btn primary" data-testid={`save-section-${s.id}`}>
                <Save size={14} strokeWidth={3} /> {saving ? "Saving..." : "Save"}
              </button>
              <button onClick={() => { setEditing(false); setHeadline(s.headline); setTimestamp(s.timestamp); setBody(s.body); setAuthor(s.author); }} className="brutal-btn">
                <X size={14} strokeWidth={3} /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <h3 className="font-mono-display text-xl font-bold tracking-tight mb-2">{s.headline}</h3>
            <p className="text-base text-slate-800 whitespace-pre-wrap leading-relaxed">{s.body}</p>
          </>
        )}
      </div>
    </li>
  );
}

function EditEpisodeForm({ ep, onCancel, onSaved }) {
  const [title, setTitle] = useState(ep.title);
  const [synopsis, setSynopsis] = useState(ep.synopsis);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!title.trim() || !synopsis.trim()) { toast.error("Title and synopsis required."); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/episodes/${ep.id}`, { title: title.trim(), synopsis: synopsis.trim() });
      toast.success("Episode updated.");
      onSaved?.(data);
    } catch (e) {
      toast.error(e.response?.status === 401 ? "Session expired. Sign in again." : "Update failed.");
    } finally { setSaving(false); }
  };

  return (
    <div className="brutal-card p-6 md:p-8 mb-6 bg-yellow-50" data-testid="edit-episode-form">
      <div className="font-mono-display text-xs uppercase tracking-[0.25em] text-pink-600 mb-3">▸ Edit Episode</div>
      <div className="space-y-4">
        <div>
          <label className="font-mono-display text-xs uppercase tracking-widest block mb-1">Title</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} className="brutal-input font-mono-display font-bold" maxLength={200} data-testid="edit-episode-title" />
        </div>
        <div>
          <label className="font-mono-display text-xs uppercase tracking-widest block mb-1">Synopsis</label>
          <textarea value={synopsis} onChange={(e) => setSynopsis(e.target.value)} rows={5} className="brutal-textarea" maxLength={5000} data-testid="edit-episode-synopsis" />
        </div>
        <div className="flex gap-2">
          <button onClick={save} disabled={saving} className="brutal-btn primary" data-testid="save-episode-button">
            <Save size={14} strokeWidth={3} /> {saving ? "Saving..." : "Save"}
          </button>
          <button onClick={onCancel} className="brutal-btn">
            <X size={14} strokeWidth={3} /> Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function ReportSectionModal({ sectionId, onClose }) {
  const [reason, setReason] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) { toast.error("Please tell us why."); return; }
    setSubmitting(true);
    try {
      await api.post(`/sections/${sectionId}/report`, {
        reason: reason.trim(),
        reporter_name: name.trim(),
      });
      toast.success("Report sent. An admin will review it.");
      onClose();
    } catch (err) {
      toast.error("Could not send report.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 flex items-center justify-center p-4" onClick={onClose} data-testid="report-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="brutal-card p-6 max-w-md w-full bg-white">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 font-mono-display text-xs uppercase tracking-[0.25em] text-red-600">
            <Flag size={14} strokeWidth={3} /> Report section
          </div>
          <button type="button" onClick={onClose} className="brutal-btn !py-1 !px-2"><X size={12} strokeWidth={3} /></button>
        </div>
        <p className="text-sm text-slate-700 mb-4">Tell us what's wrong. An admin will review this entry.</p>
        <label className="font-mono-display text-xs uppercase tracking-widest block mb-1">Reason *</label>
        <textarea
          value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="Spam, offensive language, factually wrong, off-topic..."
          rows={4} maxLength={500} className="brutal-textarea mb-3"
          data-testid="report-reason"
        />
        <label className="font-mono-display text-xs uppercase tracking-widest block mb-1">Your name (optional)</label>
        <input
          value={name} onChange={(e) => setName(e.target.value)}
          maxLength={60} className="brutal-input mb-4" placeholder="Anonymous"
          data-testid="report-name"
        />
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="brutal-btn">Cancel</button>
          <button type="submit" disabled={submitting} className="brutal-btn primary" data-testid="report-submit">
            <Flag size={14} strokeWidth={3} /> {submitting ? "Sending..." : "Send Report"}
          </button>
        </div>
      </form>
    </div>
  );
}

function AddSectionForm({ episodeId, onAdded }) {
  const [headline, setHeadline] = useState("");
  const [timestamp, setTimestamp] = useState("");
  const [body, setBody] = useState("");
  const [author, setAuthor] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!headline.trim() || !body.trim() || !timestamp.trim()) {
      toast.error("Headline, timestamp, and body are required."); return;
    }
    if (!TIMESTAMP_REGEX.test(timestamp.trim())) {
      toast.error("Timestamp must look like 12:34 or 1:02:45."); return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/episodes/${episodeId}/sections`, {
        headline: headline.trim(), timestamp: timestamp.trim(),
        body: body.trim(), author: author.trim() || "Mole Anon",
      });
      if (data?.id && data?.edit_token) {
        saveEditToken(data.id, data.edit_token);
      }
      toast.success("Section posted — you can edit it later from this browser.");
      setHeadline(""); setTimestamp(""); setBody(""); setAuthor("");
      onAdded?.();
    } catch (err) {
      toast.error("Could not add section. Try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} className="brutal-card p-6 mb-6 bg-pink-50" data-testid="add-section-form">
      <div className="font-mono-display text-xs uppercase tracking-[0.25em] text-pink-600 mb-4">▸ New Section Entry</div>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_140px] gap-4 mb-4">
        <div>
          <label className="font-mono-display text-xs uppercase tracking-widest block mb-1">Headline *</label>
          <input value={headline} onChange={(e) => setHeadline(e.target.value)} placeholder="e.g. Titus references Werewolf Bar Mitzvah" className="brutal-input" maxLength={200} data-testid="input-headline" />
        </div>
        <div>
          <label className="font-mono-display text-xs uppercase tracking-widest block mb-1">Timestamp *</label>
          <input value={timestamp} onChange={(e) => setTimestamp(e.target.value)} placeholder="12:34" className="brutal-input font-mono-display text-center" maxLength={8} data-testid="input-timestamp" />
        </div>
      </div>
      <div className="mb-4">
        <label className="font-mono-display text-xs uppercase tracking-widest block mb-1">Body *</label>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Describe the reference, joke, cameo, or callback..." rows={5} className="brutal-textarea resize-y" maxLength={5000} data-testid="input-body" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-end">
        <div>
          <label className="font-mono-display text-xs uppercase tracking-widest block mb-1">Your Name (optional)</label>
          <input value={author} onChange={(e) => setAuthor(e.target.value)} placeholder="Mole Anon" className="brutal-input" maxLength={60} data-testid="input-author" />
        </div>
        <button type="submit" disabled={submitting} className="brutal-btn primary justify-center" data-testid="submit-section-button">
          {submitting ? "Adding..." : "Post to Bunker"}
        </button>
      </div>
    </form>
  );
}
